import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, ButtonBase, Menu, MenuItem, Tooltip, Typography, TypographyProps } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PieChart } from "@mui/x-charts/PieChart";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { getDashboardData } from "../../../service/cat15.service";
import {
  AssetClass,
  Cat15Filter,
  CoverageSegment,
  DashboardData,
  IntensityCoverage,
  IntensityMetric,
  SectorEmission,
} from "../../../models/dto/cat15/cat15.types";
import Cat15FilterBar from "../../../components/cat15/Cat15FilterBar";
import StatCard from "../../../components/cat15/StatCard";
import ChartCard from "../../../components/cat15/ChartCard";
import SegmentedToggle from "../../../components/cat15/SegmentedToggle";
import IntensityLegend from "../../../components/cat15/IntensityLegend";
import { FeEcoIcon, FeWalletIcon, FeMonitoringIcon } from "../../../components/cat15/FeIcons";
import {
  assetClassLabelKey,
  intensityMetricDescKey,
  intensityMetricLabelKey,
  sectionLabel,
} from "../../../components/cat15/labels";
import SegmentedBar, { LegendRow, Segment } from "./SegmentedBar";
import VBarChart, { VBarItem } from "./VBarChart";
import ExportModal from "./ExportModal";
import {
  basisOptions,
  compareNullsLast,
  coverageState,
  coverageText,
  intensityText,
  rankShift,
  rankShiftText,
  RankRow,
  tierColor,
} from "./intensityBasis";
import { formatCompactAmount, formatNumber, formatTHB } from "../format";

// Figma Data Quality Score bar (Segment_Button 7812:17003): 1 green, 2 blue,
// 3 amber (#f59e0b), 4 orange (#f26800), 5 crimson (#d11128); "excluded" keeps
// the grey it had (#8d8d8d), which Figma does not draw.
const DQS_COLORS: Record<string, string> = {
  "1": "var(--green)",
  "2": "var(--chart-blue)",
  "3": "var(--amber)",
  "4": "var(--dqs-4)",
  "5": "var(--dqs-5)",
  excluded: "var(--gray-excluded)",
};

// Figma Coverage Disclosure bar (Stat_chart 7053:14419): included green
// (#0b9485), no emissions red (#ff4747), no financial data yellow (#ffb200), the
// rest grey (#818181). Keyed by the BE label code; a row without one (mock data)
// falls back to its position.
const COVERAGE_COLOR_BY_CODE: Record<string, string> = {
  covered: "var(--green)",
  no_emissions_data: "var(--red)",
  no_financial_data: "var(--yellow)",
};
const COVERAGE_COLORS = ["var(--green)", "var(--red)", "var(--yellow)", "var(--gray1)"];
const coverageColor = (c: CoverageSegment, i: number) =>
  (c.code ? COVERAGE_COLOR_BY_CODE[c.code] : COVERAGE_COLORS[i]) ?? "var(--gray1)";

// Figma Emissions by Asset Class donut (7053:15521): business green (#0b9485),
// listed dark (#041f3f), project indigo (#4338ca).
const ASSET_COLORS: Record<AssetClass, string> = {
  business: "var(--green)",
  listed: "var(--dark)",
  project: "var(--indigo)",
};

type Metric = "absolute" | "intensity";

const defaultFilter: Cat15Filter = {
  reportingYear: 2025,
  assetClass: "all",
  sector: [],
  listingStatus: "all",
  dqsLevel: "all",
};

// Figma's sector chart (7150:19511) draws 12 columns; "See More" shows the rest.
const TOP_SECTORS = 12;

/** Legend value — Figma Text_Input_Type value side: 12/16 semibold figure · 8 · 12/16 regular #818181 "(pct%)". */
const LegendValue = ({ value, percent }: { value: string; percent: number }) => (
  <Box display="flex" alignItems="center" gap="8px" flexShrink={0}>
    <Typography fontSize="12px" fontWeight={600} lineHeight="16px" color="var(--dark)">
      {value}
    </Typography>
    <Typography fontSize="12px" fontWeight={400} lineHeight="16px" color="var(--gray1)">
      ({percent}%)
    </Typography>
  </Box>
);

// One width per size holds the box still when the basis changes: the widest label is Thai "equity", 125px at 12px and 152px at 14px.
const BASIS_SIZES = {
  sm: { height: 24, padding: "0 2px 0 6px", radius: "8px", font: "12px", line: "16px", chevron: 16, width: 126 },
  md: { height: 40, padding: "0 6px 0 10px", radius: "8px", font: "14px", line: "20px", chevron: 20, width: 153 },
};

// MUI makes the menu's first child the list's active item; the flag skips the heading so a basis takes the focus.
const BasisMenuHead = (props: TypographyProps) => <Typography {...props} />;
BasisMenuHead.muiSkipListHighlight = true;

// A basis no included loan has a figure for stays listed but unpickable; the menu portals out of dashboard exports.
export const BasisPicker = ({
  value,
  onChange,
  coverage,
  year,
  size = "sm",
}: {
  value: IntensityMetric;
  onChange: (basis: IntensityMetric) => void;
  coverage: IntensityCoverage;
  year: number;
  /** "sm" sits at the end of a stat-card title row, "md" stands beside the chart header toggle at its 40px height */
  size?: "sm" | "md";
}) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor != null;
  const box = BASIS_SIZES[size];
  const basisLabel = (basis: IntensityMetric) => t(intensityMetricLabelKey(basis));
  return (
    <>
      <ButtonBase
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t("Basis-Select-Label")}: ${t("Basis-Per")} ${basisLabel(value)}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          height: box.height,
          padding: box.padding,
          gap: "4px",
          width: box.width,
          // a header too narrow for both controls clips this label rather than overflowing the card, so the toggle keeps its size
          minWidth: 0,
          maxWidth: "100%",
          // the title column would otherwise stretch the box across the whole card
          alignSelf: "flex-start",
          borderRadius: box.radius,
          // the border stays 1px at rest, transparent, so gaining it on hover never nudges the layout
          border: "1px solid",
          borderColor: open ? "var(--green)" : "transparent",
          backgroundColor: open ? "var(--green-05)" : "var(--gray6)",
          "&:hover": { borderColor: "var(--green)", backgroundColor: "var(--green-05)" },
          "&:focus-visible": { outline: "2px solid var(--green)", outlineOffset: "2px" },
        }}
      >
        <Typography component="span" fontSize={box.font} lineHeight={box.line} color="var(--gray9)" flexShrink={0}>
          {t("Basis-Per")}
        </Typography>
        <Typography
          component="span"
          fontSize={box.font}
          lineHeight={box.line}
          fontWeight={500}
          color="var(--dark)"
          minWidth={0}
          noWrap
          // The basis names stay lowercase for use inside sentences, so the control capitalizes its own copy.
          sx={{ textTransform: "capitalize" }}
        >
          {basisLabel(value)}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: box.chevron,
            color: "var(--gray9)",
            flexShrink: 0,
            // pinned right so the fixed-width box reads as one control whatever the label length
            marginLeft: "auto",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        MenuListProps={{ disabledItemsFocusable: true }}
      >
        <BasisMenuHead
          fontSize="12px"
          lineHeight="16px"
          fontWeight={600}
          color="var(--gray9)"
          padding="0 12px 8px"
        >
          {t("Basis-Menu-Head")}
        </BasisMenuHead>
        {basisOptions(coverage).map(({ value: basis, disabled }) => {
          const name = basisLabel(basis);
          const selected = basis === value;
          const descKey = intensityMetricDescKey(basis);
          return (
            <MenuItem
              key={basis}
              role="menuitemradio"
              // no MUI selected prop: the theme paints .Mui-selected solid green, which this row's own styling loses to
              aria-checked={selected}
              // aria-disabled plus disabledItemsFocusable keeps the arrow keys landing here, so the reason is read
              aria-disabled={disabled || undefined}
              onClick={() => {
                if (disabled) return;
                onChange(basis);
                setAnchor(null);
              }}
              sx={{
                height: "auto",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "2px",
                py: "8px",
                cursor: disabled ? "default" : "pointer",
                backgroundColor: selected ? "var(--green3)" : undefined,
                "&:hover": {
                  backgroundColor: disabled ? "transparent" : selected ? "var(--green3)" : "var(--gray6)",
                },
              }}
            >
              <Box display="flex" alignItems="center" gap="8px">
                <Typography
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight={selected ? 600 : 500}
                  color={disabled ? "var(--gray1)" : "var(--dark)"}
                  sx={{ textTransform: "capitalize" }}
                >
                  {name}
                </Typography>
                {basis === "outstanding" && (
                  <Typography
                    component="span"
                    fontSize="12px"
                    lineHeight="16px"
                    fontWeight={500}
                    color="var(--gray9)"
                    sx={{ backgroundColor: "var(--gray6)", borderRadius: "4px", px: "6px" }}
                  >
                    {t("Basis-Default")}
                  </Typography>
                )}
                {selected && <CheckIcon sx={{ fontSize: 20, color: "var(--green)", ml: "auto" }} />}
              </Box>
              <Box display="flex" alignItems="center" gap="4px">
                {disabled && <InfoOutlinedIcon sx={{ fontSize: 14, color: "var(--gray9)" }} />}
                <Typography fontSize="12px" lineHeight="16px" color="var(--gray9)">
                  {disabled ? t("Basis-No-Data", { basis: name, year }) : descKey && t(descKey)}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};


const FinancedEmissionsDashboard = () => {
  const { t, i18n } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const [filter, setFilter] = useState<Cat15Filter>(defaultFilter);
  const [data, setData] = useState<DashboardData | undefined>();
  const [sectorMetric, setSectorMetric] = useState<Metric>("absolute");
  // Each card keeps its own intensity basis across the Financed Emissions ⇄ Intensity toggle.
  const [kpiBasis, setKpiBasis] = useState<IntensityMetric>("outstanding");
  const [sectorBasis, setSectorBasis] = useState<IntensityMetric>("outstanding");
  const [showAllSectors, setShowAllSectors] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  // Full dashboard content (summary line → charts). Captured for PDF export;
  // the filter bar and modals stay outside so they don't land in the image.
  const dashboardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    getDashboardData(filter)
      .then((d) => active && setData(d))
      // handleApiError already surfaces the alert; swallow so the rejection
      // isn't unhandled (the page stays on its loading state).
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [filter]);

  // DQS legend — Figma names each row "Score n" (7053:14408). Scores 4 and 5 are
  // PCAF estimates, so their rows keep the estimate wording after the score.
  const dqsSegments: Segment[] = useMemo(
    () =>
      (data?.dqsDistribution ?? []).map((d) => ({
        label:
          d.level === "excluded"
            ? t("DQS-Excluded")
            : d.level >= 4
            ? `${t("DQS-Score", { level: d.level })} · ${t(`DQS-Name-${d.level}`)}`
            : t("DQS-Score", { level: d.level }),
        percent: d.percent,
        count: d.count,
        color: DQS_COLORS[String(d.level)] ?? "var(--gray1)",
      })),
    [data, t],
  );

  const coverageSegments: Segment[] = useMemo(
    () =>
      (data?.coverageBreakdown ?? []).map((c, i) => ({
        label: c.label,
        percent: c.percent,
        count: c.count,
        color: coverageColor(c, i),
      })),
    [data],
  );

  if (!data) {
    return (
      <Typography variant="body1" color="var(--gray1)">
        {t("Loading", { defaultValue: "Loading…" })}
      </Typography>
    );
  }

  const k = data.kpis;
  const year = data.reportingYear;
  const basisName = (basis: IntensityMetric) => t(intensityMetricLabelKey(basis));
  // Financed Emissions mode ignores the picked basis: its tooltips stay per outstanding.
  const sectorBasisShown: IntensityMetric = sectorMetric === "intensity" ? sectorBasis : "outstanding";

  // Intensity KPI under its basis: coverage decides the note and the grey.
  const kpiValue = data.intensityByMetric[kpiBasis];
  const kpiCoverage = data.intensityCoverage[kpiBasis];
  const kpiCoverageState = coverageState(kpiCoverage);
  const kpiCoverageArgs = {
    loans: kpiCoverage.loans,
    total: kpiCoverage.totalLoans,
    basis: basisName(kpiBasis),
    year,
  };
  const kpiCoverageNote = (kpiCoverageState === "partial" || kpiCoverageState === "low") && (
    <Tooltip title={t("Basis-Coverage-Full", { ...kpiCoverageArgs, pct: formatNumber(kpiCoverage.outstandingPct) })}>
      <Box component="span" display="inline-flex" alignItems="center" gap="2px" minWidth={0}>
        {kpiCoverageState === "low" && (
          <WarningAmberRoundedIcon sx={{ fontSize: 14, color: "var(--status-pending-text)", flexShrink: 0 }} />
        )}
        <Typography component="span" fontSize="12px" fontWeight={400} lineHeight="24px" color="var(--gray1)" noWrap>
          {t("Basis-Coverage-Short", { ...kpiCoverageArgs, pct: Math.round(kpiCoverage.outstandingPct) })}
        </Typography>
      </Box>
    </Tooltip>
  );

  const assetTotal = data.emissionsByAssetClass.reduce((sum, a) => sum + a.value, 0);
  const pieData = data.emissionsByAssetClass.map((a, i) => ({
    id: i,
    value: a.value,
    label: t(assetClassLabelKey(a.assetClass)),
    color: ASSET_COLORS[a.assetClass],
  }));

  // Vertical bar chart items for Emissions by Sector, with a rich hover
  // tooltip (sector + ISIC, emissions, % of total, loans, outstanding,
  // intensity). Sorted descending by the active metric.
  const totalSectorEmissions = data.emissionsBySector.reduce((sum, s) => sum + s.absolute, 0);
  const sectorIntensity = (s: SectorEmission) => s.intensityByMetric[sectorBasisShown];
  const sectionName = (s: SectorEmission) => sectionLabel(s, i18n.language, t("Sector-Unclassified"));
  const sectorScale = data.intensityScaleByMetric[sectorBasisShown];
  const knownSectorIntensities = data.emissionsBySector
    .map(sectorIntensity)
    .filter((v): v is number => v != null);
  const maxSectorIntensity = knownSectorIntensities.length ? Math.max(...knownSectorIntensities) : null;
  const sectorTruncated = data.emissionsBySector.length > TOP_SECTORS;
  const sectorVItems: VBarItem[] = [...data.emissionsBySector]
    .sort(
      sectorMetric === "absolute"
        ? (a, b) => b.absolute - a.absolute
        : compareNullsLast(sectorIntensity),
    )
    .slice(0, showAllSectors ? undefined : TOP_SECTORS)
    .map((s) => {
      const pct = Math.round((s.absolute / (totalSectorEmissions || 1)) * 1000) / 10;
      const intensity = sectorIntensity(s);
      const isHighest = intensity != null && intensity === maxSectorIntensity;
      return {
        key: s.sectionCode || sectionName(s),
        // Figma labels each column with the sector name (ellipsised, e.g.
        // "Construct…"); the ISIC code joins the full name in the tooltip.
        label: sectionName(s),
        title: sectionName(s),
        value: sectorMetric === "absolute" ? s.absolute : intensity,
        color: sectorMetric === "intensity" ? tierColor(intensity, sectorScale) : undefined,
        tooltip: (
          <Box>
            <Typography variant="body2" fontWeight={700} color="var(--white)">
              {sectionName(s)}
              {s.sectionCode ? ` (ISIC ${s.sectionCode})` : ""}
            </Typography>
            <Typography variant="body2" color="var(--white)">
              {formatNumber(s.absolute)} tCO₂e · {t("Tip-Of-Total", { pct })}
            </Typography>
            <Typography variant="body2" color="var(--white)">
              {t("loans-count", { count: s.loans ?? 0 })}
              {s.outstanding != null ? ` · ${formatTHB(s.outstanding)}` : ""} ·{" "}
              {intensity == null
                ? t("Basis-No-Data", { basis: basisName(sectorBasisShown), year })
                : `${intensityText(intensity)} ${t("Unit-Intensity")} · ${basisName(sectorBasisShown)}`}
              {isHighest ? ` · ${t("Tip-Highest-Intensity")}` : ""}
            </Typography>
          </Box>
        ),
      };
    });

  // Sector footnote: highest-emitting sector + the top-3 share of total
  // emissions, plus a "showing top N of M" note while the chart is truncated.
  const sectorsByAbs = [...data.emissionsBySector].sort((a, b) => b.absolute - a.absolute);
  const top3SectorPct =
    totalSectorEmissions > 0
      ? Math.round(
          (sectorsByAbs.slice(0, 3).reduce((sum, s) => sum + s.absolute, 0) / totalSectorEmissions) * 1000,
        ) / 10
      : 0;
  const topSectorName = sectorsByAbs[0] ? sectionName(sectorsByAbs[0]) : "";
  // In Intensity mode a basis other than outstanding adds how many loans carry its figure and how it reorders the bars.
  const basisFooter = (
    metric: Metric,
    basis: IntensityMetric,
    rows: (RankRow & { coverage: IntensityCoverage })[],
    limit: number,
  ): string[] => {
    if (metric !== "intensity") return [];
    const shift = rankShift(rows, basis, limit);
    return [
      coverageText(rows.map((r) => r.coverage[basis]), t, basisName(basis)),
      shift ? rankShiftText(shift, t, basisName(basis), limit) : "",
    ];
  };
  const sectorFooterText = data.emissionsBySector.length
    ? [
        t("Sector-Footer-Dynamic", { sector: topSectorName, pct: top3SectorPct }),
        sectorTruncated && !showAllSectors
          ? t("Sector-Footer-Truncated", { shown: TOP_SECTORS, total: data.emissionsBySector.length })
          : "",
        ...basisFooter(
          sectorMetric,
          sectorBasis,
          data.emissionsBySector.map((s) => ({
            label: sectionName(s),
            byMetric: s.intensityByMetric,
            coverage: s.intensityCoverage,
          })),
          showAllSectors ? data.emissionsBySector.length : TOP_SECTORS,
        ),
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  // Figma's toggle (7150:17983) reads "Financed Emissions | Intensity".
  const metricOptions = [
    { value: "absolute", label: t("KPI-Financed-Emissions") },
    { value: "intensity", label: t("Toggle-Intensity") },
  ];

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      {/* Filter bar (Figma 4173:71) */}
      <Cat15FilterBar
        value={filter}
        onChange={setFilter}
        onReset={() => setFilter(defaultFilter)}
        onExport={() => setOpenExport(true)}
      />
      {/* Figma column (4173:70): 20px between the summary, stat cards and charts */}
      <Box ref={dashboardRef} display="flex" flexDirection="column" gap="20px">
        {/* Summary line (Figma 4173:166): 14/21 regular #6b7280. Needs Attention is
            hidden for now; ActionDetailModal stays for when it comes back. */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="16px" minHeight={21}>
          <Typography fontSize="14px" fontWeight={400} lineHeight="21px" color="var(--gray9)">
            {t("FE-Subtitle", {
              count: data.loanCount,
              outstanding: formatTHB(data.totalOutstanding),
            })}
          </Typography>
        </Box>

        {/* Stat cards (Figma Stat_card 4173:169): three 116px cards, 16px apart */}
        <Box display="flex" flexWrap="wrap" gap="16px">
          <Box flex="1 1 0" minWidth={280}>
            <StatCard
              title={t("KPI-Financed-Emissions")}
              value={formatNumber(k.financedEmissions.value)}
              unit="tCO₂e"
              info={t("KPI-Financed-Emissions-Info")}
              icon={<FeEcoIcon />}
              iconColor="var(--green)"
              iconBg="var(--tint-green)"
            />
          </Box>
          <Box flex="1 1 0" minWidth={280}>
            <StatCard
              title={t("KPI-Outstanding")}
              value={formatCompactAmount(k.outstanding.value)}
              unit="THB"
              info={t("KPI-Outstanding-Info")}
              icon={<FeWalletIcon />}
              iconColor="var(--amber)"
              iconBg="var(--tint-amber)"
            />
          </Box>
          <Box flex="1 1 0" minWidth={280}>
            <StatCard
              title={t("KPI-Intensity")}
              // CCSME26-389 basis picker: Figma has no slot, so it closes the title row
              titleAction={
                <Box display="flex" flexShrink={0}>
                  <BasisPicker
                    value={kpiBasis}
                    onChange={setKpiBasis}
                    coverage={data.intensityCoverage}
                    year={year}
                  />
                </Box>
              }
              value={kpiValue == null ? "—" : formatNumber(kpiValue)}
              unit={t("Unit-Intensity")}
              muted={kpiCoverageState === "low"}
              info={
                kpiBasis === "outstanding"
                  ? t("KPI-Intensity-Info")
                  : t("KPI-Intensity-Info-Basis", { basis: basisName(kpiBasis) })
              }
              icon={<FeMonitoringIcon />}
              iconColor="var(--purple)"
              iconBg="var(--tint-purple)"
              // Figma shows no YoY pills; the coverage note stays so a thin basis is never read as complete.
              valueAside={kpiCoverageNote}
            />
          </Box>
        </Box>

        {/* Charts (Figma container 7053:14383): 16px gaps throughout */}
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap="16px">
            {/* LEFT — Emissions by Asset Class (Figma 7053:15511, 558px) */}
            <Box flex="1 1 0" minWidth={400}>
              <ChartCard
                title={t("Chart-Emissions-By-Asset-Class")}
                info={t("Chart-Emissions-By-Asset-Class-Info")}
              >
                {/* Figma 7053:15515: a centred 20px-gap column. Its 36px padding
                    is overrun by the content (472px in a 481px box), which Figma
                    centres — leaving 4.5px above the donut and below the note. */}
                <Box display="flex" flexDirection="column" alignItems="center" gap="20px" py="4.5px">
                  {/* Donut — Figma Pie_chart 7053:15516: 300px */}
                  <Box position="relative" flexShrink={0} width={300} height={300}>
                    <PieChart
                      series={[
                        {
                          data: pieData,
                          innerRadius: 88,
                          outerRadius: 148,
                          paddingAngle: 1,
                        },
                      ]}
                      width={300}
                      height={300}
                      margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                      slotProps={{ legend: { hidden: true } }}
                    />
                    {/* Centre total (Figma 7053:15517) */}
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      textAlign="center"
                      sx={{ transform: "translate(-50%, -50%)", pointerEvents: "none" }}
                    >
                      <Typography fontSize="11.782px" fontWeight={400} lineHeight="16.848px" color="var(--gray1)">
                        {t("Chart-Total")}
                      </Typography>
                      <Typography fontSize="17.673px" fontWeight={600} lineHeight="23.564px" color="var(--dark)">
                        {formatNumber(assetTotal)}
                      </Typography>
                      <Typography fontSize="11.782px" fontWeight={400} lineHeight="16.848px" color="var(--gray1)">
                        tCO₂e
                      </Typography>
                    </Box>
                  </Box>

                  {/* Legend rows (Figma 7053:15525) */}
                  <Box display="flex" flexDirection="column" gap="4px" width="100%">
                    {data.emissionsByAssetClass.map((a) => (
                      <LegendRow
                        key={a.assetClass}
                        color={ASSET_COLORS[a.assetClass]}
                        label={t(assetClassLabelKey(a.assetClass))}
                        value={<LegendValue value={`${formatNumber(a.value)} tCO₂e`} percent={a.percent} />}
                      />
                    ))}
                  </Box>

                  {/* PCAF note (Figma HorizontalBorder 7053:15570, 52px): the 1px #f3f6fb rule sits inside Figma's 16px top padding, so 1 + 15 + 36 */}
                  <Box width="100%" pt="15px" sx={{ borderTop: "1px solid var(--gray6)" }}>
                    <Typography fontSize="12px" fontWeight={400} lineHeight={1.5} color="var(--dark)">
                      {t("Asset-Class-Note")}
                    </Typography>
                  </Box>
                </Box>
              </ChartCard>
            </Box>

            {/* RIGHT — Data Quality Score + Coverage Disclosure (Figma 7053:14402) */}
            <Box flex="1 1 0" minWidth={400} display="flex" flexDirection="column" gap="16px">
              <ChartCard title={t("Chart-Data-Quality-Score")} info={t("Chart-DQS-Distribution-Info")}>
                <SegmentedBar segments={dqsSegments} legendValue="count-percent" minLabelPercent={5} />
              </ChartCard>
              <ChartCard title={t("Sec-Coverage")} info={t("Chart-Coverage-Breakdown-Info")}>
                <SegmentedBar segments={coverageSegments} legendValue="count-percent" />
              </ChartCard>
            </Box>
          </Box>

          {/* BOTTOM — Emissions by Sector (Figma 7053:14433, full width) */}
          <ChartCard
            size="lg"
            title={t("Chart-Emissions-By-Sector")}
            info={t("Chart-Emissions-By-Sector-Info")}
            action={
              <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
                {/* CCSME26-389 basis picker: the picker comes first so appearing in Intensity mode grows leftwards and leaves the toggle put */}
                {sectorMetric === "intensity" && (
                  <BasisPicker
                    size="md"
                    value={sectorBasis}
                    onChange={setSectorBasis}
                    coverage={data.intensityCoverage}
                    year={year}
                  />
                )}
                <SegmentedToggle
                  options={metricOptions}
                  value={sectorMetric}
                  onChange={(v) => setSectorMetric(v as Metric)}
                />
              </Box>
            }
          >
            <VBarChart
              items={sectorVItems}
              unit={sectorMetric === "intensity" ? t("Unit-Intensity") : "tCO₂e"}
            />
            {sectorMetric === "intensity" && sectorScale && <IntensityLegend scale={sectorScale} />}
            {/* Bottom row (Figma 7053:16116): "See More" right-aligned 16px below
                the chart; the sector footnote shares the row on the left. */}
            <Box display="flex" alignItems="flex-start" gap="16px" mt="16px">
              <Typography flex={1} minWidth={0} fontSize="14px" fontWeight={400} lineHeight="24px" color="var(--gray1)">
                {sectorFooterText}
              </Typography>
              {sectorTruncated && (
                <Button
                  variant="text"
                  color="green"
                  onClick={() => setShowAllSectors((v) => !v)}
                  data-html2canvas-ignore
                  sx={{
                    p: 0,
                    minWidth: 0,
                    flexShrink: 0,
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "24px",
                    textTransform: "none",
                    "&:hover": { bgcolor: "transparent" },
                  }}
                >
                  {showAllSectors ? t("See-Less") : t("See-More")}
                </Button>
              )}
            </Box>
          </ChartCard>
        </Box>
      </Box>


      <ExportModal
        open={openExport}
        onClose={() => setOpenExport(false)}
        data={data}
        targetRef={dashboardRef}
      />
    </Box>
  );
};

export default FinancedEmissionsDashboard;
